import React, { useState } from "react";
import { X, Cake, PartyPopper, Calendar, MapPin, Mail, Send, Sparkles, CheckCircle2 } from "lucide-react";
import { K } from "../theme";
import { Av } from "./Avatar";
import { Bt } from "./Button";
import { Bg } from "./Badge";
import { notify } from "../notify";
import api from "../api";
import { fmtDMY } from "../dateFormat";

export const CelebrationModal = ({ item, onClose }) => {
  const [sendState, setSendState] = useState("idle"); // idle | sending | sent
  if (!item) return null;

  const isBirthday = item.type === "birthday" || (!item.type && !!item.dob);

  // Calculate event timing details
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const currentYear = today.getFullYear();

  let eventMonth = 0;
  let eventDay = 1;

  if (item.date && item.date.includes("-")) {
    const parts = item.date.split("-");
    eventMonth = parseInt(parts[0], 10) - 1;
    eventDay = parseInt(parts[1], 10);
  } else if (item.dob) {
    const d = new Date(item.dob);
    eventMonth = d.getMonth();
    eventDay = d.getDate();
  } else if (item.join_date) {
    const d = new Date(item.join_date);
    eventMonth = d.getMonth();
    eventDay = d.getDate();
  }

  let eventDateThisYear = new Date(currentYear, eventMonth, eventDay);
  if (eventDateThisYear < today && (today - eventDateThisYear) > 30 * 86400000) {
    eventDateThisYear = new Date(currentYear + 1, eventMonth, eventDay);
  }

  const diffTime = eventDateThisYear.getTime() - today.getTime();
  const diffDays = Math.round(diffTime / (1000 * 3600 * 24));

  let timingLabel = "";
  if (diffDays === 0) timingLabel = "Today! 🎉";
  else if (diffDays === 1) timingLabel = "Tomorrow ⏳";
  else if (diffDays > 1) timingLabel = `In ${diffDays} Days`;
  else timingLabel = `${Math.abs(diffDays)} Days Ago`;

  const formattedEventDate = fmtDMY(eventDateThisYear);

  const formattedJoinDate = item.join_date ? fmtDMY(item.join_date) : null;

  const primaryColor = isBirthday ? K.ro : K.pu;
  const gradientHeader = isBirthday
    ? `linear-gradient(135deg, ${K.ro} 0%, #e11d48 100%)`
    : `linear-gradient(135deg, ${K.pu} 0%, #7c3aed 100%)`;

  const handleSendWish = async () => {
    if (sendState === "sending" || sendState === "sent") return;
    setSendState("sending");
    try {
      await api.post(`/api/employees/${item.employee_id}/wish`, {
        type: isBirthday ? "birthday" : "anniversary",
        years: item.years,
      });
      setSendState("sent");
      setTimeout(onClose, 1900);
    } catch (err) {
      setSendState("idle");
      notify(err.message || "Failed to send wishes", { title: "Error", variant: "error" });
    }
  };

  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/50 backdrop-blur-sm z-[250] flex items-center justify-center p-4 transition-all" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b0 rounded-2xl w-full max-w-[684px] overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200" onMouseDown={e => e.stopPropagation()}>
        {/* Banner Header */}
        <div className="relative p-6 text-white text-center overflow-hidden" style={{ background: gradientHeader }}>
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -left-8 -bottom-8 w-28 h-28 rounded-full bg-white/10" />
          <button onClick={onClose} className="absolute top-3 right-3 text-white/80 hover:text-white bg-black/20 hover:bg-black/40 rounded-full p-1 border-0 cursor-pointer transition-colors">
            <X size={16} />
          </button>

          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md mb-2 shadow-inner">
            {isBirthday ? <Cake size={26} className="text-white" /> : <PartyPopper size={26} className="text-white" />}
          </div>

          <h2 className="text-xl font-bold m-0">{isBirthday ? "Birthday Celebration" : "Work Anniversary"}</h2>
          <div className="text-white/80 text-xs mt-1 font-medium flex items-center justify-center gap-1">
            <Sparkles size={13} /> {timingLabel}
          </div>
        </div>

        {/* Body Details */}
        <div className="p-5 sm:p-6 space-y-4">
          {/* Employee Info Header */}
          <div className="flex items-center gap-4 bg-bg2/60 p-3 rounded-xl border border-b0">
            <Av nm={item.name || "?"} sz={52} rn={primaryColor} src={item.photo} />
            <div className="flex-1 min-w-0">
              <div className="text-base font-bold text-t0 truncate">{item.name}</div>
              <div className="text-xs text-t3 font-medium">ID: {item.employee_id}</div>
              {(item.designation || item.department) && (
                <div className="text-xs text-t2 truncate mt-0.5">
                  {item.designation}{item.designation && item.department ? " · " : ""}{item.department}
                </div>
              )}
            </div>
          </div>

          {/* Event Details Card */}
          <div className="bg-bg1 rounded-xl border border-b0 p-4 space-y-3 shadow-sm">
            <div className="text-xs uppercase font-bold text-t3 tracking-wider flex items-center gap-1.5">
              <Calendar size={13} style={{ color: primaryColor }} /> Event Details
            </div>

            <div className="flex justify-between items-center bg-bg2 p-3 rounded-lg">
              <div>
                <div className="text-xs text-t3">Event Date</div>
                <div className="text-sm font-semibold text-t0 mt-0.5">{formattedEventDate}</div>
              </div>
              <Bg c={primaryColor} sm dot>{timingLabel}</Bg>
            </div>

            {!isBirthday && (
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="bg-bg2 p-2.5 rounded-lg">
                  <span className="text-t3 block">Tenure Completed</span>
                  <span className="text-t0 font-bold text-sm mt-0.5 block">{item.years} {item.years === 1 ? 'Year' : 'Years'}</span>
                </div>
                {formattedJoinDate && (
                  <div className="bg-bg2 p-2.5 rounded-lg">
                    <span className="text-t3 block">Joining Date</span>
                    <span className="text-t0 font-semibold mt-0.5 block truncate">{formattedJoinDate}</span>
                  </div>
                )}
              </div>
            )}

            {isBirthday && (
              <div className="bg-bg2 p-2.5 rounded-lg text-xs">
                <span className="text-t3 block">Birthday</span>
                <span className="text-t0 font-semibold mt-0.5 block">
                  {eventDateThisYear.toLocaleDateString("en-IN", { month: "long", day: "numeric" })}
                </span>
              </div>
            )}
          </div>

          {/* Additional Info (Office & Email) */}
          <div className="space-y-2 text-xs">
            {item.office && (
              <div className="flex items-center gap-2 text-t2">
                <MapPin size={14} className="text-t3 flex-shrink-0" />
                <span className="truncate">{item.office}</span>
              </div>
            )}
            {item.email && (
              <div className="flex items-center gap-2 text-t2">
                <Mail size={14} className="text-t3 flex-shrink-0" />
                <a href={`mailto:${item.email}`} className="text-p hover:underline truncate">{item.email}</a>
              </div>
            )}
          </div>

          {/* Actions */}
          {sendState === "sent" ? (
            <div
              className="flex items-center justify-center gap-2 rounded-xl py-3 px-4 font-bold text-white animate-in fade-in zoom-in-95 duration-200"
              style={{ background: `linear-gradient(135deg, ${primaryColor}, ${isBirthday ? "#e11d48" : "#7c3aed"})` }}
            >
              <CheckCircle2 size={18} className="animate-bounce" />
              Wishes sent to {(item.name || "").split(" ")[0] || "them"}!
              <Sparkles size={15} className="animate-pulse" />
            </div>
          ) : (
            <div className="flex gap-2 pt-2">
              <Bt full style={{ background: primaryColor }} icon={Send} disabled={sendState === "sending"} onClick={handleSendWish}>
                {sendState === "sending" ? "Sending…" : "Send Wishes"}
              </Bt>
              <Bt v="secondary" onClick={onClose}>
                Close
              </Bt>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
